import React, { useState, useEffect } from 'react';
import { useLanguages } from '../../hooks/useLanguages';
import { useProfile } from '../../context/ProfileContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import './ProjectLanguageSelector.css';

interface ProjectLanguageSelectorProps {
  onLanguageSelect?: (language: string) => void;
}

const ProjectLanguageSelector: React.FC<ProjectLanguageSelectorProps> = ({ onLanguageSelect }) => {
  const { selectedProject, selectedTeam } = useWorkspace();
  const { profileData } = useProfile();
  const { user } = useAuth();
  const {
    availableLanguages,
    isLoading,
    error
  } = useLanguages();
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>();

  // Check if current user is team owner
  const isTeamOwner = selectedTeam?.owner_profile_id === profileData?.id;

  // Initialize selected language from localStorage or first available language
  useEffect(() => {
    if (!selectedProject || availableLanguages.length === 0) return;

    const savedLanguage = user ? localStorage.getItem(`${user.id}_selectedLanguage_${selectedProject.id}`) : null;
    const languageToSelect = savedLanguage && availableLanguages.includes(savedLanguage) 
      ? savedLanguage 
      : availableLanguages[0];

    setSelectedLanguage(languageToSelect);
    onLanguageSelect?.(languageToSelect);
  }, [selectedProject, availableLanguages, user]);

  if (!selectedProject || !selectedTeam) {
    return null;
  }

  const handleLanguageClick = (language: string) => {
    setSelectedLanguage(language);
    onLanguageSelect?.(language);
    
    // Store the selection in localStorage
    if (user && selectedProject) {
      localStorage.setItem(`${user.id}_selectedLanguage_${selectedProject.id}`, language);
    }
  };

  return (
    <LanguageSelector
      languages={availableLanguages}
      selectedLanguage={selectedLanguage}
      isOwner={isTeamOwner}
      onLanguageClick={handleLanguageClick}
    />
  );
};

export default ProjectLanguageSelector; 